import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Function to detect browser timezone
const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback to UTC if timezone detection fails
    return 'UTC';
  }
};

export interface Meeting {
  id: string;
  applicantIds: string[];
  setApplicantIds: (applicantIds: string[]) => void;
  scheduledAt: Date;
  setScheduledAt: (time: Date) => void;
  duration: number;
  setDuration: (duration: number) => void;
  location: string;
  setLocation: (location: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
}

interface MeetingProviderProps {
  children: React.ReactNode;
  initialAddress?: string;
}

const MeetingContext = createContext<Meeting | undefined>(undefined);

export const MeetingProvider: React.FC<MeetingProviderProps> = ({ children, initialAddress = '' }) => {
  const [applicantIds, setApplicantIds] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState<Date>(new Date());
  const [duration, setDuration] = useState<number>(2 * 60 * 60 * 1000);
  const [location, setLocation] = useState<string>(initialAddress);
  const [notes, setNotes] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  
  // Generate a stable ID that doesn't change on re-renders
  const [meetingId] = useState(() => crypto.randomUUID());

  // Detect and set browser timezone on component mount
  useEffect(() => {
    const browserTimezone = detectBrowserTimezone();
    setTimezone(browserTimezone);
  }, []);

  // Update location if initialAddress changes
  useEffect(() => {
    if (initialAddress) {
      setLocation(initialAddress);
    }
  }, [initialAddress]);

  // Memoize the setter functions to prevent unnecessary re-renders
  const setApplicantIdsMemo = useCallback((ids: string[]) => {
    setApplicantIds(ids);
  }, []);

  const setScheduledAtMemo = useCallback((time: Date) => {
    setScheduledAt(time);
  }, []);

  const setDurationMemo = useCallback((duration: number) => {
    setDuration(duration);
  }, []);

  const setLocationMemo = useCallback((location: string) => {
    setLocation(location);
  }, []);

  const setNotesMemo = useCallback((notes: string) => {
    setNotes(notes);
  }, []);

  const setTimezoneMemo = useCallback((timezone: string) => {
    setTimezone(timezone);
  }, []);

  const value: Meeting = useMemo(() => ({
    id: meetingId,
    applicantIds,
    setApplicantIds: setApplicantIdsMemo,
    scheduledAt,
    setScheduledAt: setScheduledAtMemo,
    duration,
    setDuration: setDurationMemo,
    location,
    setLocation: setLocationMemo,
    notes,
    setNotes: setNotesMemo,
    timezone,
    setTimezone: setTimezoneMemo,
  }), [meetingId, applicantIds, scheduledAt, duration, location, notes, timezone, setApplicantIdsMemo, setScheduledAtMemo, setDurationMemo, setLocationMemo, setNotesMemo, setTimezoneMemo]);

  return (
    <MeetingContext.Provider value={value}>
      {children}
    </MeetingContext.Provider>
  );
};

export const useMeeting = () => {
  const ctx = useContext(MeetingContext);
  if (!ctx) throw new Error('useMeeting must be used within a MeetingProvider');
  
  return ctx;
};